import { useMemo } from 'react'

import type { UserType, EnhancedNotificationItem } from '@/features/notifications'
import { useRealTimeNotifications } from '@/hooks/notifications/useRealTimeNotifications'

export interface UseNotificationsState {
    notifications: EnhancedNotificationItem[]
    userType: UserType
    isLoading: boolean
    isDismissingAll: boolean
    error: string | null
    lastNotificationId: string | null
    hasNewNotification: boolean
    // Pagination properties
    totalNotifications: number
    loadedNotifications: number
    hasMoreNotifications: boolean
    isLoadingMore: boolean
}

export interface UseNotificationsActions {
    setUserType: (userType: UserType) => void
    dismissNotification: (notificationId: string) => void
    clearAllNotifications: () => void
    refreshNotifications: () => Promise<void>
    refreshNotificationsList: () => Promise<void>
    loadMoreNotifications: () => Promise<void>
}

export interface UseNotificationsReturn {
    state: UseNotificationsState
    actions: UseNotificationsActions
    unreadCount: number
}

export function useNotifications(
    initialUserType: UserType = 'manager',
    playNotification: () => void,
): UseNotificationsReturn {
    const realNotifications = useRealTimeNotifications(initialUserType, playNotification)

    // Simple hash function for stable URL mapping
    const hashNotificationId = (id: string): number => {
        return Math.abs(Array.from(id).reduce((acc, char) => ((acc << 5) - acc + char.charCodeAt(0)) | 0, 0))
    }

    // Enhance backend notifications with fallback URLs only when API data is missing/invalid
    // Use useMemo to prevent unnecessary re-computation on every render
    const enhancedNotifications = useMemo(() => {
        // Fallback URLs for when API doesn't provide them - defined inside useMemo to avoid dependency issues
        const workingUrls = ['/dashboard', '/profile', '/team', '/upload', '/notification-settings']
        const workingLabels = ['Go to Dashboard', 'View Profile', 'View Team', 'View Uploads', 'Settings']

        return realNotifications.state.notifications.map((notification) => {
            // Only enhance if action button exists but data is missing/invalid
            if (notification.has_action_button) {
                const hasValidUrl =
                    notification.internal_url &&
                    notification.internal_url.trim() !== '' &&
                    notification.internal_url !== 'null'

                const hasValidLabel =
                    notification.button_label &&
                    notification.button_label.trim() !== '' &&
                    notification.button_label !== 'null'

                // If API provides valid data, trust it completely
                if (hasValidUrl && hasValidLabel) {
                    return notification
                }

                // Otherwise, apply fallback for safety (prevents React crashes)
                const urlIndex = hashNotificationId(notification.id) % workingUrls.length
                return {
                    ...notification,
                    internal_url: hasValidUrl ? notification.internal_url : workingUrls[urlIndex],
                    button_label: hasValidLabel ? notification.button_label : workingLabels[urlIndex],
                }
            }
            return notification
        })
    }, [realNotifications.state.notifications])

    const enhancedState = {
        ...realNotifications.state,
        notifications: enhancedNotifications,
    }

    return {
        ...realNotifications,
        state: enhancedState,
    }
}
